// hooks/useFidCardFunnelTracking.js
import { usePostHog } from 'posthog-js/react';
import { useState, useCallback } from 'react';

export const FID_CARD_FUNNEL_STEPS = {
  INITIAL_CHOICE: 'fid_initial_choice',
  CREATE_NAME: 'fid_create_name',
  CREATE_CIVILITY: 'fid_create_civility',
  CREATE_BIRTHDATE: 'fid_create_birthdate',
  CREATE_EMAIL: 'fid_create_email',
  CREATE_PHONE: 'fid_create_phone',
  CREATE_STORE: 'fid_create_store',
  CREATE_SUBMIT: 'fid_create_submit',
  SUCCESS: 'fid_success',
  ERROR: 'fid_error',
};

const STEP_ORDER = {
  [FID_CARD_FUNNEL_STEPS.INITIAL_CHOICE]: 1,
  [FID_CARD_FUNNEL_STEPS.CREATE_NAME]: 2,
  [FID_CARD_FUNNEL_STEPS.CREATE_CIVILITY]: 3,
  [FID_CARD_FUNNEL_STEPS.CREATE_BIRTHDATE]: 4,
  [FID_CARD_FUNNEL_STEPS.CREATE_EMAIL]: 5,
  [FID_CARD_FUNNEL_STEPS.CREATE_PHONE]: 6,
  [FID_CARD_FUNNEL_STEPS.CREATE_STORE]: 7,
  [FID_CARD_FUNNEL_STEPS.CREATE_SUBMIT]: 8,
  [FID_CARD_FUNNEL_STEPS.SUCCESS]: 9,
  [FID_CARD_FUNNEL_STEPS.ERROR]: 9,
};

const TOTAL_STEPS = 9;

const getFunnelStepOrder = (stepName) => {
  return STEP_ORDER[stepName] || 0;
};

const getFunnelProgress = (stepName) => {
  const currentOrder = getFunnelStepOrder(stepName);
  return Math.round((currentOrder / TOTAL_STEPS) * 100);
};

export const useFidCardFunnelTracking = () => {
  const posthog = usePostHog();
  const [stepStartTime, setStepStartTime] = useState(null);
  const [funnelStartTime, setFunnelStartTime] = useState(null);

  const trackStep = useCallback((stepName, metadata = {}) => {
    const now = Date.now();
    const timeSpent = stepStartTime ? now - stepStartTime : 0;
    const totalTimeInFunnel = funnelStartTime ? now - funnelStartTime : 0;

    posthog?.capture(`fid_card_${stepName}`, {
      step_name: stepName,
      step_order: getFunnelStepOrder(stepName),
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      time_spent_on_step_ms: timeSpent,
      time_spent_on_step_seconds: Math.round(timeSpent / 1000),
      total_time_in_funnel_ms: totalTimeInFunnel,
      total_time_in_funnel_seconds: Math.round(totalTimeInFunnel / 1000),
      ...metadata,
    });

    setStepStartTime(now);
  }, [posthog, stepStartTime, funnelStartTime]);

  const trackPageView = useCallback((stepName, metadata = {}) => {
    trackStep(stepName, metadata);

    posthog?.capture('$pageview', {
      $current_url: window.location.href,
      page_title: document.title,
      funnel_type: 'fidelity_card_creation',
      funnel_step: stepName,
      step_order: getFunnelStepOrder(stepName),
      funnel_progress_percentage: getFunnelProgress(stepName),
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }, [trackStep, posthog]);

  const initFunnel = useCallback(() => {
    const now = Date.now();
    setFunnelStartTime(now);
    setStepStartTime(now);

    posthog?.capture('fid_card_funnel_started', {
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
    });
  }, [posthog]);

  const trackAbandonment = useCallback((currentStep, reason = 'unknown', metadata = {}) => {
    const totalTimeInFunnel = funnelStartTime ? Date.now() - funnelStartTime : 0;

    posthog?.capture('fid_card_funnel_abandoned', {
      abandonment_step: currentStep,
      step_order: getFunnelStepOrder(currentStep),
      abandonment_reason: reason,
      funnel_progress_percentage: getFunnelProgress(currentStep),
      total_time_in_funnel_ms: totalTimeInFunnel,
      total_time_in_funnel_seconds: Math.round(totalTimeInFunnel / 1000),
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      ...metadata,
    });
  }, [posthog, funnelStartTime]);

  const trackValidationError = useCallback((stepName, errors, metadata = {}) => {
    const errorFields = Object.keys(errors).filter(key => errors[key]);

    posthog?.capture('fid_card_validation_error', {
      step_name: stepName,
      step_order: getFunnelStepOrder(stepName),
      error_fields: errorFields,
      error_count: errorFields.length,
      errors: errors,
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }, [posthog]);

  const trackFieldInteraction = useCallback((fieldName, stepName, metadata = {}) => {
    posthog?.capture('fid_card_field_interaction', {
      field_name: fieldName,
      step_name: stepName,
      step_order: getFunnelStepOrder(stepName),
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }, [posthog]);

  const trackSubmitAttempt = useCallback((metadata = {}) => {
    trackStep(FID_CARD_FUNNEL_STEPS.CREATE_SUBMIT, {
      submit_attempt: true,
      ...metadata,
    });
  }, [trackStep]);

  const trackSuccess = useCallback((metadata = {}) => {
    const totalTimeInFunnel = funnelStartTime ? Date.now() - funnelStartTime : 0;

    posthog?.capture('fid_card_funnel_completed', {
      completion_time: new Date().toISOString(),
      funnel_success: true,
      total_steps: TOTAL_STEPS,
      funnel_progress_percentage: 100,
      total_time_in_funnel_ms: totalTimeInFunnel,
      total_time_in_funnel_seconds: Math.round(totalTimeInFunnel / 1000),
      total_time_in_funnel_minutes: Math.round(totalTimeInFunnel / 60000),
      ...metadata,
    });

    trackPageView(FID_CARD_FUNNEL_STEPS.SUCCESS, metadata);
  }, [posthog, funnelStartTime, trackPageView]);

  const trackError = useCallback((errorMessage, errorDetails = {}, metadata = {}) => {
    const totalTimeInFunnel = funnelStartTime ? Date.now() - funnelStartTime : 0;

    posthog?.capture('fid_card_funnel_error', {
      error_message: errorMessage,
      error_code: errorDetails.errorCode,
      is_server_error: errorDetails.isServerError,
      should_retry: errorDetails.shouldRetry,
      total_time_in_funnel_ms: totalTimeInFunnel,
      total_time_in_funnel_seconds: Math.round(totalTimeInFunnel / 1000),
      timestamp: new Date().toISOString(),
      ...metadata,
    });

    trackPageView(FID_CARD_FUNNEL_STEPS.ERROR, {
      error_message: errorMessage,
      ...errorDetails,
      ...metadata,
    });
  }, [posthog, funnelStartTime, trackPageView]);

  const trackBackNavigation = useCallback((fromStep, toStep, metadata = {}) => {
    posthog?.capture('fid_card_back_navigation', {
      from_step: fromStep,
      from_step_order: getFunnelStepOrder(fromStep),
      to_step: toStep,
      to_step_order: getFunnelStepOrder(toStep),
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }, [posthog]);

  const trackRetryAttempt = useCallback((metadata = {}) => {
    posthog?.capture('fid_card_retry_attempt', {
      timestamp: new Date().toISOString(),
      ...metadata,
    });
  }, [posthog]);

  return {
    trackPageView,
    trackStep,
    initFunnel,

    trackPageViewInitialChoice: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.INITIAL_CHOICE, data),
    trackPageViewCreateName: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_NAME, data),
    trackPageViewCreateCivility: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_CIVILITY, data),
    trackPageViewCreateBirthdate: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_BIRTHDATE, data),
    trackPageViewCreateEmail: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_EMAIL, data),
    trackPageViewCreatePhone: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_PHONE, data),
    trackPageViewCreateStore: (data = {}) => trackPageView(FID_CARD_FUNNEL_STEPS.CREATE_STORE, data),

    trackAbandonment,
    trackValidationError,
    trackFieldInteraction,
    trackSubmitAttempt,
    trackSuccess,
    trackError,
    trackBackNavigation,
    trackRetryAttempt,

    posthog,
  };
};
